import { useState } from 'react';

import { AccountGroup, AccountSaveRequest, AccountType, DataType, LifecycleStatus } from '@ndvr/portfolio-api';

import { Spinner } from '@/components/Spinner';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { titleCase } from 'utils';
import { requiresBirthYear } from 'views/account/account-constants';
import { useCreateAccount } from 'views/portfolio/account-queries';

interface CreateAccountModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Fixed target portfolio. Omit to let the user pick one from `accountGroups`. */
    accountGroup?: AccountGroup;
    /** Portfolios to choose from when `accountGroup` is not given. */
    accountGroups?: AccountGroup[];
}

const isOpenPortfolio = (group: AccountGroup) =>
    group.lifecycleStatus !== LifecycleStatus.CLOSED && !group.originAccountGroupGUID;

export const CreateAccountModal = ({ isOpen, onClose, accountGroup, accountGroups = [] }: CreateAccountModalProps) => {
    const selectableAccountGroups = accountGroups.filter(isOpenPortfolio);
    const [selectedAccountGroupGUID, setSelectedAccountGroupGUID] = useState<string>(
        selectableAccountGroups.length === 1 ? selectableAccountGroups[0].accountGroupGUID : '',
    );
    const targetAccountGroup =
        accountGroup ?? selectableAccountGroups.find((group) => group.accountGroupGUID === selectedAccountGroupGUID);
    const showPortfolioSelect = !accountGroup;

    const [accountName, setAccountName] = useState('');
    const [accountType, setAccountType] = useState<AccountType | ''>('');
    const [accountOwnerBirthYear, setAccountOwnerBirthYear] = useState('');
    const [externallyManaged, setExternallyManaged] = useState(false);
    const [forecastStartingValue, setForecastStartingValue] = useState('');

    const { mutate: createAccount, isPending } = useCreateAccount();

    const accountTypeRequiresBirthYear = requiresBirthYear(accountType || null);

    const isValidForecastValue = (value: string) => {
        if (!value?.trim()) return false;
        const numValue = Number(value);
        return !isNaN(numValue) && numValue >= 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (
            targetAccountGroup &&
            accountName.trim() &&
            accountType &&
            (!accountTypeRequiresBirthYear || accountOwnerBirthYear) &&
            (!externallyManaged || isValidForecastValue(forecastStartingValue))
        ) {
            const request: AccountSaveRequest = {
                accountName: {
                    plainText: accountName.trim(),
                    dataType: DataType.PORTFOLIO_NAME,
                    encryptionContextGUID: targetAccountGroup.householdGUID,
                },
                accountType,
                externallyManaged,
                forecastStartingValue: externallyManaged ? forecastStartingValue.trim() : '0',
                taxLossHarvesting: false,
                ...(accountTypeRequiresBirthYear &&
                    accountOwnerBirthYear && {
                        accountOwnerBirthYear: parseInt(accountOwnerBirthYear),
                    }),
            };
            createAccount(
                { accountGroupGUID: targetAccountGroup.accountGroupGUID, request },
                {
                    onSuccess: () => {
                        setAccountName('');
                        setAccountType('');
                        setAccountOwnerBirthYear('');
                        setExternallyManaged(false);
                        setForecastStartingValue('');
                        onClose();
                    },
                },
            );
        }
    };

    const handleCancel = () => {
        setAccountName('');
        setAccountType('');
        setAccountOwnerBirthYear('');
        setExternallyManaged(false);
        setForecastStartingValue('');
        onClose();
    };

    const isValid =
        !!targetAccountGroup &&
        accountName.trim() &&
        accountType &&
        (!accountTypeRequiresBirthYear || accountOwnerBirthYear) &&
        (!externallyManaged || isValidForecastValue(forecastStartingValue)) &&
        !isPending;

    return (
        <Dialog open={isOpen} onOpenChange={handleCancel}>
            <DialogContent className="sm:max-w-[425px]">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle>Create Account</DialogTitle>
                        <DialogDescription></DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        {showPortfolioSelect && (
                            <div className="grid gap-2">
                                <Label htmlFor="accountGroup">Portfolio</Label>
                                <Select
                                    value={selectedAccountGroupGUID}
                                    onValueChange={setSelectedAccountGroupGUID}
                                    disabled={isPending}
                                >
                                    <SelectTrigger id="accountGroup">
                                        <SelectValue placeholder="Select portfolio" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {selectableAccountGroups.map((group) => (
                                            <SelectItem key={group.accountGroupGUID} value={group.accountGroupGUID}>
                                                {group.accountGroupName?.plainText || 'Account Group'}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="grid gap-2">
                            <Label htmlFor="accountName">Account Name</Label>
                            <Input
                                id="accountName"
                                value={accountName}
                                onChange={(e) => setAccountName(e.target.value)}
                                placeholder="Enter account name"
                                disabled={isPending}
                                required
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="accountType">Account Type</Label>
                            <Select
                                value={accountType}
                                onValueChange={(value) => setAccountType(value as AccountType)}
                                disabled={isPending}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select account type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.values(AccountType).map((type) => (
                                        <SelectItem key={type} value={type}>
                                            {titleCase(type.replace(/_/g, ' '))}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {accountTypeRequiresBirthYear && (
                            <div className="grid gap-2">
                                <Label htmlFor="accountOwnerBirthYear">Account Owner Birth Year</Label>
                                <Input
                                    id="accountOwnerBirthYear"
                                    type="number"
                                    value={accountOwnerBirthYear}
                                    onChange={(e) => setAccountOwnerBirthYear(e.target.value)}
                                    placeholder="Enter birth year (e.g., 1980)"
                                    min="1900"
                                    max={new Date().getFullYear()}
                                    disabled={isPending}
                                    required
                                />
                            </div>
                        )}
                        <div className="flex items-center space-x-2">
                            <Switch
                                id="externallyManaged"
                                checked={externallyManaged}
                                onCheckedChange={setExternallyManaged}
                                disabled={isPending}
                            />
                            <Label htmlFor="externallyManaged">Held Away</Label>
                        </div>
                        {externallyManaged && (
                            <div className="grid gap-2">
                                <Label htmlFor="forecastStartingValue">Held Away AUM</Label>
                                <Input
                                    id="forecastStartingValue"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={forecastStartingValue}
                                    onChange={(e) => setForecastStartingValue(e.target.value)}
                                    placeholder="Enter starting value (e.g., 100000.00)"
                                    disabled={isPending}
                                    required
                                />
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={handleCancel} disabled={isPending}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!isValid}>
                            {isPending ? (
                                <>
                                    <Spinner size="sm" />
                                    Creating...
                                </>
                            ) : (
                                'Create Account'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
